/**
 * Placeholder registry context object and `usePlaceholders` hook.
 *
 * `.ts` (no JSX) so `PlaceholderProvider.tsx` stays a Fast Refresh component boundary.
 */

import { createContext, useContext } from 'react';
import type { PlaceholderInfo } from '../types';

/**
 * Fetched `PlaceholderInfo` rows and refresh control for all components that
 * render `{{...}}` tokens with server-driven descriptions and examples.
 */
export interface PlaceholderContextValue {
  /** Registry rows; empty on error or before first load. */
  placeholders: PlaceholderInfo[];
  isLoading: boolean;
  loadError: Error | null;
  /** Re-fetches from `/cardtemplates/placeholders` (e.g. after admin save). */
  refresh: () => Promise<void>;
}

/**
 * React context object. Exported for `PlaceholderProvider` only — consumers use `usePlaceholders`.
 */
export const PlaceholderContext = createContext<PlaceholderContextValue | null>(null);

/**
 * Returns placeholder registry state; if used outside the provider, returns
 * an empty list so unit tests and stories can mount `TemplateText` in isolation.
 */
export function usePlaceholders(): PlaceholderContextValue {
  const ctx = useContext(PlaceholderContext);
  if (!ctx) {
    return {
      placeholders: [],
      isLoading: false,
      loadError: null,
      refresh: async () => {},
    };
  }
  return ctx;
}
